import { Link } from 'react-router-dom';
import useEmblaCarousel from 'embla-carousel-react';
import { WheelGesturesPlugin } from 'embla-carousel-wheel-gestures';

import { SanityImage } from '@/components/content/SanityImage';
import { Icon } from '@/lib/Icon';
import { track } from '@/lib/analytics';
import {
  cart,
  cartItemFromProduct,
  useCart,
} from '@/features/marketplace/stores/cart';
import { cartUi } from '@/features/marketplace/stores/cartUi';
import type {
  MarketplaceProduct,
  MarketplaceRecommendation,
} from '@/types/api';

interface RecommendedProduct {
  product: MarketplaceProduct;
  recommendation: MarketplaceRecommendation;
}

interface Props {
  items: RecommendedProduct[];
  recommendationModel: string | null;
}

function money(cents: number | null | undefined): string | null {
  return typeof cents === 'number' ? `$${Math.round(cents / 100)}` : null;
}

export function PersonalizedProductsCard({ items, recommendationModel }: Props) {
  // Subscribe to the cart so each recommendation CTA updates immediately
  // after the drawer is closed, removed, or changed elsewhere in the app.
  const cartItems = useCart((state) => state.items);

  const trackEvent = (event: string, item?: RecommendedProduct) => {
    track(event, {
      product_id: item?.product.id ?? null,
      canonical_product_id: item?.product.canonical_product_id ?? null,
      recommendation_rank: item?.recommendation.rank ?? null,
      recommendation_model: recommendationModel,
      source: 'home_recommendations_card',
    });
  };

  const handleStackAction = (item: RecommendedProduct, inCart: boolean) => {
    if (inCart) {
      cartUi.open();
      trackEvent('personalized_recommendations_home_in_cart_opened', item);
      return;
    }

    const added = cart.add(cartItemFromProduct(item.product));
    if (!added) return;
    cartUi.open();
    trackEvent('personalized_recommendations_home_add_to_stack', item);
  };

  const count = String(items.length).padStart(2, '0');
  // WheelGestures adds trackpad / mouse-wheel horizontal scroll on top of
  // Embla's drag/swipe (Embla doesn't handle wheel events natively).
  const [emblaRef] = useEmblaCarousel(
    { align: 'start', containScroll: 'trimSnaps' },
    [WheelGesturesPlugin()],
  );

  return (
    <section className="flex flex-col bg-[var(--surface-sunken)] pt-5 pb-15 text-13 text-ink w-full">
      <div className="max-w-[65rem] mx-auto w-full">
        <div className="flex items-center gap-8 font-mono uppercase px-15 md:px-40">
          <span className="">[{count}]</span>
          <h2 className="">
            New health recommendations
          </h2>
        </div>
  
        {/* Embla carousel — drag/swipe + horizontal scroll. Full-bleed: the
            container carries the column gutter so the first card aligns with
            the title and the last peeks off the edge. Full card on mobile with
            a peek of the next, two-up from sm. */}
        <div className="overflow-hidden mt-12" ref={emblaRef}>
          <div className="flex gap-10 px-15 md:px-40">
          {items.map((item) => {
            const { product } = item;
            const inCart = cartItems.some((cartItem) => cartItem.id === product.id);
            const price = money(product.price_cents);
            const compare = money(product.renewal_price_cents);
            const showCompare =
              compare != null &&
              typeof product.renewal_price_cents === 'number' &&
              typeof product.price_cents === 'number' &&
              product.renewal_price_cents > product.price_cents;
            // Match the shop tab's ProductCard subtitle exactly
            // (treatment_support, falling back to subtitle) rather than the
            // recommendation reason / quantity.
            const subtitle = product.treatment_support || product.subtitle;
  
            return (
              <article
                key={product.id}
                className="flex-[0_0_80%] xs:flex-[0_0_70%] sm:flex-[0_0_calc(50%-0.5rem)] min-w-0 flex flex-col border border-[#ECECEC] bg-white"
              >
                <Link
                  to={`/app/shop?detail=${encodeURIComponent(product.slug)}`}
                  onClick={() => trackEvent('personalized_recommendations_home_clicked', item)}
                  className="relative block aspect-[1] overflow-hidden bg-sunken bg-[url('/icons/system_scene_background.jpg')] bg-cover bg-center"
                >
                  {/* Zero-background product render composited over the shared
                      scene backdrop (same pattern as the stack card). Falls back
                      to the standard hero thumbnail when no transparent render
                      exists yet. */}
                  {product.scene_image_url ? (
                    <img
                      src={product.scene_image_url}
                      alt={product.title}
                      loading="eager"
                      className="absolute inset-0 h-full w-full object-contain px-15 py-50"
                    />
                  ) : (
                    <SanityImage
                      url={product.hero_image_url}
                      image={product.hero_image}
                      width={640}
                      height={800}
                      alt={product.title}
                      className="absolute inset-0 h-full w-full object-cover"
                    />
                  )}
  
                  {/* Price tag — top left. */}
                  {price && (
                    <div className="absolute top-5 left-5 flex items-baseline gap-2 bg-black/50 backdrop-blur-[20px] px-10 py-10 text-12 leading-100 text-white">
                      {showCompare && (
                        <span className="text-white/50 line-through">{compare}</span>
                      )}
                      <span className="text-14">{price}</span>
                      <span className="text-10">
                        {product.purchase_mode === 'one_time_course'
                          ? 'one-time course'
                          : '/ first mo.'}
                      </span>
                    </div>
                  )}
  
                  {/* Title + subtitle — overlaid at the bottom of the image. */}
                  <div className="absolute inset-x-0 bottom-0">
                    <div className="bg-black/50 px-10 py-10 text-white backdrop-blur-[20px]">
                      <div className="font-mono text-15 uppercase leading-100 text-balance">
                        {product.title}
                      </div>
                      {subtitle && (
                        <div className="mt-5 font-body text-12 leading-snug text-white/80">
                          {subtitle}
                        </div>
                      )}
                    </div>
                  </div>
                </Link>
  
                {/* Add to stack — once present, mirror Shop's muted In cart
                    treatment while remaining clickable to reopen the drawer. */}
                <button
                  type="button"
                  onClick={() => handleStackAction(item, inCart)}
                  className={`flex items-center justify-between gap-10 h-44 px-15 bg-black font-body text-14 text-white ${
                    inCart ? 'opacity-50 hover:opacity-60' : 'hover:opacity-90'
                  }`}
                >
                  <span>{inCart ? 'In cart' : 'Add to stack'}</span>
                  <span aria-hidden className="inline-flex items-center justify-center w-8">
                    <Icon name="Arrow" viewBox="0 0 10 12" />
                  </span>
                </button>
              </article>
            );
          })}
          </div>
        </div>
      </div>
    </section>
  );
}
